/**
 * ADR-106 Ф0: модель Authority-слоя — полномочия как контент.
 *
 * Модель **доменно-нейтральна**: «действие», «цель» и «роль» — данные, а не код
 * ядра (ADR-106 §«Доменная нейтральность»). Ядро знает только форму записи и
 * правила решения; конкретные глаголы (`sign`, `commit`), цели (`record:*`,
 * `invoice:*`) и роли (`clinician`, `treasurer`) приезжают контентом
 * (`provides.authority` пакета, ADR-086/ADR-090) и включаются политикой
 * инстанса (ADR-106 §«Правила безопасности контента»).
 *
 * Ключевые инварианты (ADR-106 §«Инварианты модели»):
 * 1. **Нет гранта — нет права.** `allow` не выводится из отсутствия запрета;
 *    неизвестное действие/цель — `deny` (fail-closed).
 * 2. **Грант — всегда «действие × цель».** Цель — типизированный ресурс; текст
 *    команды, промпт и имя тула целью не являются.
 * 3. **Права только сужаются** (пересечение осей, не объединение).
 *
 * Схемы — **строгие** (`.strict()`): неизвестное поле, неизвестное действие,
 * неизвестная цель или неизвестная роль — ошибка разбора, а не «проскочивший»
 * passthrough. Кросс-ссылки (грант → объявленное действие, агент → объявленная
 * роль) проверяет `validateAuthorityContent`.
 *
 * Модуль browser-safe (`zod` + локальные типы + `utils/glob`): используется и в
 * ядре, и (в будущем) в UI.
 */
import { z } from 'zod';
import { globMatch } from '../utils/glob.js';
// --- Виды принципалов ---
/**
 * Кто действует (ADR-106 §«Модель»): пользователь, агент, service-key,
 * шедулер или внешний источник события.
 */
export const AUTHORITY_PRINCIPAL_KINDS = ['user', 'agent', 'service', 'scheduler', 'event'];
export const authorityPrincipalKindSchema = z.enum(AUTHORITY_PRINCIPAL_KINDS);
/**
 * Идентификатор принципала: `user:<id>`, `agent:<id>`, `service:<keyId>`,
 * `scheduler`, `event:<source>` (ADR-106 §«Многопользовательская сессия»).
 */
export const AUTHORITY_PRINCIPAL_ID_REGEX = /^[a-z][a-z0-9._-]*:[^\s]+$/;
export const authorityPrincipalRefSchema = z
    .string()
    .min(3)
    .max(256)
    .regex(AUTHORITY_PRINCIPAL_ID_REGEX, 'принципал: "<вид>:<идентификатор>" (например, user:local)');
// --- Виды целей ---
/**
 * Виды целей, известные **ядру** (ADR-106 §«Модель»): проект, путь, сессия,
 * MCP-сервер, внешний ресурс, рабочая область.
 *
 * ⚠️ Доменная нейтральность требует и других видов (`record:<patientId>`,
 * `invoice:<id>`, `report:<period>` — ADR-106 §«Доменная нейтральность»), поэтому
 * `kind` — идентификатор строки, а не закрытое перечисление: доменные виды
 * **объявляются контентом** (`provides.authority.targets[]`) и становятся
 * известными только после установки и одобрения пакета. Ядро знает базовый
 * набор (`AUTHORITY_CORE_TARGET_KINDS`) и не более.
 */
export const AUTHORITY_CORE_TARGET_KINDS = [
    'project',
    'path',
    'session',
    'mcp',
    'external',
    'workspace',
    // ADR-106 Ф2: путь **вне** рабочего каталога проекта. Отдельный вид цели:
    // правило `external_directory` и fail-closed дефолт «спросить» относятся
    // именно к таким путям, а к `path:` (внутри workspace) — не относятся.
    // Ссылка цели — неразличимый токен (хэш абсолютного пути): сырой путь в
    // журнал решений и метрики не попадает (инвариант 25, ADR-094), а
    // сопоставление с шаблоном гранта идёт по `altRefs` (абсолютный путь).
    'outside',
];
/** Идентификатор вида цели (ядро + доменные, объявленные контентом). */
export const AUTHORITY_TARGET_KIND_REGEX = /^[a-z][a-z0-9._-]*$/;
export const authorityTargetKindSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(AUTHORITY_TARGET_KIND_REGEX, 'вид цели: строчные латинские буквы/цифры (a-z0-9._-)');
/** true — вид цели известен ядру (без контента). */
export function isCoreTargetKind(kind) {
    return AUTHORITY_CORE_TARGET_KINDS.includes(kind);
}
// --- Действия ---
/**
 * Идентификатор доменного действия: `read`/`write`/`execute`/`publish`/
 * `approve`/`sign`/`delegate`/`commit` — **данные**, а не перечисление ядра.
 */
export const AUTHORITY_ACTION_ID_REGEX = /^[a-z][a-z0-9._-]*$/;
export const authorityActionIdSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(AUTHORITY_ACTION_ID_REGEX, 'действие: строчные латинские буквы/цифры (a-z0-9._-)');
/**
 * Действия, **не** меняющие состояние (ADR-106 Ф5, trust-floor).
 *
 * Мутирующим считается **всё, кроме `read`** (fail-closed): доменные действия
 * по умолчанию мутирующие. Признак `mutating` в манифесте пакета —
 * **информационный**: ему не доверяют, иначе пакет объявил бы своё мутирующее
 * действие «безобидным» и обошёл бы trust-floor (мутирующие гранты
 * активируются только при trust ≥ `trusted`).
 */
export const AUTHORITY_READ_ONLY_ACTIONS = ['read'];
/** Ядровые мутирующие действия (перечислены для документации и UI). */
export const AUTHORITY_MUTATING_ACTIONS = [
    'write',
    'execute',
    'commit',
    'publish',
    'approve',
    'sign',
    'delegate',
];
/** true — действие мутирующее (меняет состояние). */
export function isAuthorityMutatingAction(id) {
    return !AUTHORITY_READ_ONLY_ACTIONS.includes(id);
}
// --- Цели и шаблоны целей ---
/**
 * Шаблон цели гранта: `<вид>:<шаблон-ссылки>` (ADR-106 §«Модель»).
 * Примеры: `path:src/**`, `path:*.env`, `mcp:git/*`, `record:*`,
 * `external:registry/*`.
 *
 * Вид обязателен: цель — типизированный ресурс, а не «просто строка».
 */
export const AUTHORITY_TARGET_PATTERN_REGEX = /^[a-z][a-z0-9._-]*:.+$/;
export const authorityTargetPatternSchema = z
    .string()
    .min(3)
    .max(256)
    .regex(AUTHORITY_TARGET_PATTERN_REGEX, 'шаблон цели: "<вид>:<шаблон>" (например, path:src/**)');
// --- Эффекты и ограничения гранта ---
/** Эффект гранта: разрешить / спросить / запретить. */
export const AUTHORITY_GRANT_EFFECTS = ['allow', 'ask', 'deny'];
export const authorityGrantEffectSchema = z.enum(AUTHORITY_GRANT_EFFECTS);
/**
 * Ограничения гранта (ADR-106 §«Модель»): `once` (одноразовость),
 * `ttlMs` (срок жизни), `maxCount` (лимит применений), `requireConfirm`
 * (требовать подтверждения даже при `allow`).
 */
export const authorityGrantConstraintsSchema = z
    .object({
    /** Грант одноразовый: повторное применение — `deny`. */
    once: z.boolean().optional(),
    /** Срок жизни гранта, мс. */
    ttlMs: z.number().int().positive().max(31_536_000_000).optional(),
    /** Предел числа применений гранта. */
    maxCount: z.number().int().positive().max(1_000_000).optional(),
    /** Даже `allow` требует подтверждения носителем гранта. */
    requireConfirm: z.boolean().optional(),
})
    .strict();
// --- Сущности модели ---
/**
 * Принципал — тот, от чьего имени исполняется ход (ADR-106 §«Модель»).
 * `roles[]` — роли, которые носитель принципала предъявляет (содержательные
 * роли приезжают контентом; глобальные роли инстанса — отдельная ось).
 */
export const authorityPrincipalSchema = z
    .object({
    kind: authorityPrincipalKindSchema,
    id: z.string().min(1).max(256),
    roles: z.array(z.string().min(1).max(128)).default([]),
})
    .strict();
/**
 * Действие — доменный глагол. `mutating` — признак изменения состояния;
 * `requires[]` — действия, которые нужны перед этим (например, `sign`
 * требует `write`).
 */
export const authorityActionSchema = z
    .object({
    id: authorityActionIdSchema,
    mutating: z.boolean(),
    requires: z.array(authorityActionIdSchema).default([]),
})
    .strict();
/** Цель — типизированный ресурс. */
export const authorityTargetSchema = z
    .object({
    kind: authorityTargetKindSchema,
    ref: z.string().min(1).max(512),
    /** Область действия (например, проект/схема), необязательна. */
    scope: z.string().min(1).max(256).optional(),
})
    .strict();
/**
 * Грант — разрешение «действие × цель» с эффектом и опциональными
 * ограничениями. `deny` в гранте сильнее `allow` (ADR-106 §«Резолвинг и
 * решение»).
 */
export const authorityGrantSchema = z
    .object({
    action: authorityActionIdSchema,
    targetPattern: authorityTargetPatternSchema,
    effect: authorityGrantEffectSchema,
    constraints: authorityGrantConstraintsSchema.optional(),
})
    .strict();
/** Роль — именованный набор грантов (поставляется контентом). */
export const authorityRoleSchema = z
    .object({
    id: z.string().min(1).max(128),
    displayName: z.string().min(1).max(256).optional(),
    domain: z.string().min(1).max(128),
    grants: z.array(authorityGrantSchema).default([]),
})
    .strict();
/** Источники потолка (верхней границы активных прав). */
export const AUTHORITY_CEILING_SOURCES = ['user', 'session', 'pack', 'instance'];
export const authorityCeilingSourceSchema = z.enum(AUTHORITY_CEILING_SOURCES);
/**
 * Потолок — верхняя граница активных прав. Потолок с пустым списком грантов
 * **задаёт** запрет на всё (ось применима, грантов нет → «нет гранта — нет
 * права»); отсутствие потолка означает, что ось не применима.
 */
export const authorityCeilingSchema = z
    .object({
    grants: z.array(authorityGrantSchema).default([]),
    source: authorityCeilingSourceSchema,
})
    .strict();
/**
 * Ход — единица исполнения. Принципал фиксируется **при приёме ввода**
 * (ADR-106 §«Инварианты модели», п. 4) — в Ф4; в Ф0–Ф1 это просто контракт.
 */
export const authorityTurnSchema = z
    .object({
    id: z.string().min(1).max(128),
    sessionId: z.string().min(1).max(128),
    principal: authorityPrincipalSchema,
    /** Агент хода (если ход исполняет агент). */
    agentId: z.string().min(1).max(128).optional(),
    /** Потолки хода (сессия/пакет/инстанс). */
    ceiling: z.array(authorityCeilingSchema).default([]),
    startedAt: z.string().min(1),
})
    .strict();
/**
 * Подтверждение — запрос на действие, привязанный к
 * `(principal, turn, action, target, digest)`; одноразовый (`once`).
 */
export const authorityApprovalSchema = z
    .object({
    id: z.string().min(1).max(128),
    turnId: z.string().min(1).max(128),
    principal: authorityPrincipalSchema,
    action: authorityActionIdSchema,
    target: authorityTargetSchema,
    /** Отпечаток запроса (действие + цель + ход) — защита от подмены. */
    digest: z.string().min(1).max(256),
    once: z.boolean(),
    expiresAt: z.string().min(1).optional(),
})
    .strict();
/**
 * Объявление вида цели контентом: `kind` + имя резолвера (резолверы целей
 * регистрирует ядро или плагин — ADR-106 §«Декларация, не код»).
 */
export const authorityTargetDeclarationSchema = z
    .object({
    kind: authorityTargetKindSchema,
    resolver: z.string().min(1).max(128).optional(),
})
    .strict();
/**
 * Привязка агента к ролям и потолку (ADR-106 Ф5): контент пакета объявляет,
 * какие роли носит агент (`roles[]`) и какие действия ему потолок
 * (`ceiling[]` — идентификаторы действий).
 *
 * Роли — из `policy.roles` (их гранты уже прошли пересечение с политикой
 * инстанса и trust-floor), потолок — **запись** о намерении пакета; принуждение
 * оси `pack` по нему — остаток (ADR-106 §11).
 */
export const authorityPolicyAgentSchema = z
    .object({
    /** id агента. */
    id: z.string().min(1).max(128),
    /** Роли агента (id из `policy.roles`). */
    roles: z.array(z.string().min(1).max(128)).default([]),
    /** Потолок агента — идентификаторы действий. */
    ceiling: z.array(z.string().min(1).max(64)).default([]),
})
    .strict();
/**
 * Политика полномочий инстанса — верхняя граница (ADR-106 §«Точки
 * принуждения», уровень 4). Хранится через порт `AuthorityPolicyRepository`
 * (ADR-096).
 */
export const authorityPolicySchema = z
    .object({
    /** Объявленные действия (доменные глаголы). */
    actions: z.array(authorityActionSchema).default([]),
    /** Объявленные роли. */
    roles: z.array(authorityRoleSchema).default([]),
    /** Виды целей, известные инстансу (ядро + объявленные контентом). */
    targets: z.array(authorityTargetDeclarationSchema).default([]),
    /** Потолок инстанса. */
    ceilings: z.array(authorityCeilingSchema).default([]),
    /** Привязки агентов к ролям и потолку (ADR-106 Ф5). */
    agents: z.array(authorityPolicyAgentSchema).default([]),
})
    .strict();
// --- Запись журнала решений ---
/**
 * Запись append-only журнала решений Authority-слоя (ADR-106 §«Инварианты
 * модели», п. 8): `principal`/`action`/`target`/`effect`/`source` — **без
 * секретов и без сырых путей** (путь нормализуется до относительного внутри
 * workspace, инвариант 25/ADR-094).
 */
export const authorityDecisionEntrySchema = z
    .object({
    id: z.string().min(1).max(128),
    ts: z.string().min(1),
    /** Принципал в виде `<вид>:<идентификатор>`. */
    principal: z.string().min(1).max(256),
    action: z.string().min(1).max(64),
    /** Цель в виде `<вид>:<ссылка>` (нормализованная). */
    target: z.string().min(1).max(512),
    effect: authorityGrantEffectSchema,
    /** Ось, давшая решение (`user`/`agent`/`session`/`pack`/`instance`). */
    source: z.string().min(1).max(32),
    /** Причина: `unknown`/`explicit`/`no_grant`/null. */
    reason: z.string().min(1).max(64).nullable(),
    /** Режим: `shadow` — решение посчитано, но не применено (Ф1). */
    mode: z.enum(['shadow', 'enforced']).default('shadow'),
    /**
     * ADR-106 Ф1: решение permission-слоя на момент shadow-записи. Нужно для
     * разбора расхождений миграции («shadow-решение не совпало с текущим —
     * это дефект миграции»). В enforced-режиме поле отсутствует.
     */
    permissionEffect: z.enum(['allow', 'ask', 'deny']).optional(),
})
    .strict();
/**
 * Проверка целостности контента — **список проблем строками** (как
 * `validatePackManifest`), без throw.
 *
 * Проверяется:
 * 1. дубликаты id действий и ролей;
 * 2. грант ссылается на **объявленное** действие (неизвестное действие — отказ);
 * 3. `action.requires[]` ссылается на объявленные действия;
 * 4. вид цели в шаблоне гранта — известен ядру или объявлен в `targets[]`
 *    (неизвестная цель — отказ);
 * 5. агент ссылается на объявленные роли и на объявленные действия в потолке.
 */
export function validateAuthorityContent(content) {
    const problems = [];
    const actionIds = new Set();
    content.actions.forEach((action, index) => {
        if (actionIds.has(action.id)) {
            problems.push(`actions[${index}]: дубликат id действия «${action.id}»`);
            return;
        }
        actionIds.add(action.id);
    });
    const roleIds = new Set();
    content.roles.forEach((role, index) => {
        if (roleIds.has(role.id)) {
            problems.push(`roles[${index}]: дубликат id роли «${role.id}»`);
            return;
        }
        roleIds.add(role.id);
    });
    const targetKinds = new Set(AUTHORITY_CORE_TARGET_KINDS);
    content.targets.forEach((target, index) => {
        if (targetKinds.has(target.kind)) {
            problems.push(`targets[${index}]: вид цели «${target.kind}» уже известен ядру`);
            return;
        }
        targetKinds.add(target.kind);
    });
    content.actions.forEach((action, index) => {
        action.requires.forEach((required, reqIndex) => {
            if (!actionIds.has(required)) {
                problems.push(`actions[${index}].requires[${reqIndex}]: неизвестное действие «${required}»`);
            }
        });
    });
    content.roles.forEach((role, index) => {
        role.grants.forEach((grant, grantIndex) => {
            if (!actionIds.has(grant.action)) {
                problems.push(`roles[${index}].grants[${grantIndex}]: неизвестное действие «${grant.action}»`);
            }
            const kind = targetKindOfPattern(grant.targetPattern);
            if (kind !== undefined && !targetKinds.has(kind)) {
                problems.push(`roles[${index}].grants[${grantIndex}]: неизвестный вид цели «${kind}»`);
            }
        });
    });
    (content.agents ?? []).forEach((agent, index) => {
        agent.roles.forEach((roleId, roleIndex) => {
            if (!roleIds.has(roleId)) {
                problems.push(`agents[${index}].roles[${roleIndex}]: неизвестная роль «${roleId}»`);
            }
        });
        (agent.ceiling ?? []).forEach((actionId, actionIndex) => {
            if (!actionIds.has(actionId)) {
                problems.push(`agents[${index}].ceiling[${actionIndex}]: неизвестное действие «${actionId}»`);
            }
        });
    });
    return problems;
}
/** Вид цели из шаблона (`path:src/**` → `path`); `undefined` — шаблон без вида. */
export function targetKindOfPattern(pattern) {
    const idx = pattern.indexOf(':');
    if (idx <= 0)
        return undefined;
    return pattern.slice(0, idx);
}
// --- Матчинг целей ---
/** Строковое представление цели: `<вид>:<ссылка>`. */
export function formatAuthorityTarget(target) {
    return `${target.kind}:${target.ref}`;
}
/**
 * Совпадает ли шаблон гранта с целью.
 *
 * Шаблон разбирается на вид и шаблон ссылки: вид обязан совпасть, ссылка
 * сравнивается glob'ом (`*` — сегмент, `**` — путь, ADR-029). Дополнительные
 * формы ссылки (`altRefs`) проверяются тоже — так же, как permission-слой
 * применяет файловый паттерн и к абсолютному, и к относительному пути.
 *
 * Несовпадение вида → `false` (fail-closed): грант на `path:*` не покрывает
 * цель `session:*`.
 */
export function matchAuthorityTargetPattern(pattern, target, altRefs) {
    const idx = pattern.indexOf(':');
    if (idx <= 0)
        return false;
    const kind = pattern.slice(0, idx);
    if (kind !== target.kind)
        return false;
    const refPattern = pattern.slice(idx + 1);
    if (refPattern === '')
        return false;
    if (globMatch(refPattern, target.ref))
        return true;
    for (const alt of altRefs ?? []) {
        if (globMatch(refPattern, alt))
            return true;
    }
    return false;
}
